import { useCallback, useEffect, useRef, useState } from 'react';
import { withSignal } from '../api/client';
import { onChange, type ChangeNotice } from './changes';

export interface DataState<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  reload: () => void;
}

/** Live reloads of one screen: at most one every few seconds. */
const LIVE_GAP_MS = 3000;
/** Several changes in a row (parts of an import) load once. */
const SETTLE_MS = 300;

/** The company the page is about (/t/<domain>/...), or null on Stitch Admin's pages. */
function pageTenant(): string | null {
  const m = /^\/t\/([^/]+)/.exec(window.location.pathname);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]!).toLowerCase();
  } catch {
    return null;
  }
}

/** Whether a change elsewhere concerns what this page shows. */
export function concernsPage(notice: ChangeNotice, here: string | null = pageTenant()): boolean {
  return !notice.tenant || !here || notice.tenant.toLowerCase() === here;
}

/**
 * Load data for a page; keeps showing the last result while reloading. It loads again by itself
 * when something changes on the phone system for the company on screen (Leland applies a plan, a
 * change is undone, another tab changes something): pass `live: false` for data that doesn't
 * follow the phone system, or a form's starting values.
 *
 * - A tab in the background loads once it's looked at again, not before.
 * - `topics`: also loads again when one of these happens (a call ended: 'calls').
 * - A newer load cancels the older one's reads.
 * - Another subject (company, person) starts afresh: the last one's data isn't shown for it.
 */
export function useData<T>(load: (signal: AbortSignal) => Promise<T>, deps: readonly unknown[], opts: { live?: boolean; topics?: readonly 'calls'[] } = {}): DataState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const hasData = useRef(false);
  /** A reload because something changed elsewhere: a failure keeps what's shown. */
  const quiet = useRef(false);
  const lastDeps = useRef<readonly unknown[] | null>(null);

  useEffect(() => {
    let alive = true;
    // Another subject: what was shown belongs to the last one.
    const prev = lastDeps.current;
    if (prev && (prev.length !== deps.length || prev.some((d, i) => !Object.is(d, deps[i])))) {
      hasData.current = false;
      setData(undefined);
      setError(undefined);
    }
    lastDeps.current = deps;
    const background = quiet.current && hasData.current;
    quiet.current = false;
    setLoading(true);
    const abort = new AbortController();
    let pending: Promise<T>;
    try {
      pending = withSignal(abort.signal, () => loadRef.current(abort.signal));
    } catch (e) {
      pending = Promise.reject(e);
    }
    pending
      .then((d) => {
        if (!alive) return;
        hasData.current = true;
        setData(d);
        setError(undefined);
      })
      .catch((e: unknown) => {
        if (!alive || background) return;
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
      abort.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const live = opts.live !== false;
  const topics = opts.topics ?? [];
  useEffect(() => {
    if (!live) return;
    let timer: number | undefined;
    let last = 0;
    let waiting = false;
    const go = () => {
      window.clearTimeout(timer);
      const wait = Math.max(SETTLE_MS, last + LIVE_GAP_MS - Date.now());
      timer = window.setTimeout(() => {
        // Not while the tab is in the background: when it's looked at again.
        if (document.visibilityState === 'hidden') {
          waiting = true;
          return;
        }
        last = Date.now();
        quiet.current = true;
        setTick((t) => t + 1);
      }, wait);
    };
    const off = onChange((notice) => {
      if (!concernsPage(notice)) return;
      // A change about one thing (a call ended) is only for the screens showing it.
      if (notice.topic && !topics.includes(notice.topic)) return;
      if (document.visibilityState === 'hidden') {
        waiting = true;
        return;
      }
      go();
    });
    const visible = () => {
      if (document.visibilityState === 'visible' && waiting) {
        waiting = false;
        go();
      }
    };
    document.addEventListener('visibilitychange', visible);
    return () => {
      off();
      document.removeEventListener('visibilitychange', visible);
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, topics.join(',')]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}
